<template>
    <normal-page name="Shop">
        <div
            v-if="status === 'loading'"
            class="flex flex-col items-center justify-center w-full mt-16 gap-3"
        >
            <div class="h-12 w-12 animate-spin rounded-full border border-foam/10 border-t-foam/70" />
            <div class="eyebrow">
                Fetching items from the store...
            </div>
        </div>

        <div
            v-else-if="!items.length"
            class="mt-10 w-full text-center font-serif text-2xl font-light italic"
        >
            No merch available right now.
        </div>

        <div
            v-else
            class="grid gap-6 sm:grid-cols-2 lg:gap-8 xl:grid-cols-3"
        >
            <a
                v-for="(item, i) in items"
                :key="item.id || i"
                :href="item.url"
                target="_blank"
                rel="noopener"
                class="panel group mx-auto flex w-full max-h-[27rem] max-w-[544px] flex-col p-5 transition-colors duration-300 hover:border-foam/15 sm:p-6"
            >
                <div class="relative mx-auto w-fit max-h-48">
                    <v-lazy-image
                        :src="artSrc(item.imageSrc)"
                        class="image-glow max-h-48 rounded-md"
                    />

                    <div
                        v-if="item.soldOut"
                        class="absolute inset-0 flex items-center justify-center bg-void/70 text-[11px] uppercase tracking-[0.3em] backdrop-blur-sm"
                    >
                        Sold out
                    </div>
                </div>

                <h2
                    v-text="item.title"
                    class="mt-5 flex-1 font-serif text-2xl font-light italic tracking-wide transition-colors group-hover:text-white"
                />

                <div class="mt-4 grid grid-cols-2 gap-x-6 border-t border-foam/5 pt-3">
                    <div>
                        <div class="eyebrow">Type</div>
                        <div v-text="item.type" class="mt-1 text-base text-foam/70" />
                    </div>

                    <div class="text-right">
                        <div class="eyebrow">Price</div>
                        <div class="mt-1 text-base text-foam/70">{{ item.price }} {{ item.currency }}</div>
                    </div>
                </div>
            </a>
        </div>
    </normal-page>
</template>

<script setup>
    import { onBeforeUnmount, onMounted, ref } from 'vue';
    import VLazyImage from 'v-lazy-image';
    import NormalPage from '@/components/layout/pages/NormalPage';
    import {
        clearMerchCache,
        fetchMerch,
        isMerchCacheStale,
        readMerchCache,
        writeMerchCache,
    } from '@/library/fetchMerch';
    import bakedMerch from '@/data/merch';

    const artSrc = (src) => src.replace(/_\d+\.jpg$/, '_16.jpg');

    const items = ref([]);
    const status = ref('loading');
    const controller = new AbortController();

    let disposed = false;
    onMounted(async () => {
        const cached = readMerchCache();

        if (cached) {
            items.value = cached.items;
            status.value = 'ready';

            if (isMerchCacheStale(cached)) {
                try {
                    const result = await fetchMerch(controller.signal);

                    writeMerchCache(result);
                    items.value = result;
                } catch (error) {
                    if (!disposed) {
                        clearMerchCache();
                    }
                } finally {
                    controller.abort();
                }
            }

            return;
        }

        try {
            const result = await fetchMerch(controller.signal);

            writeMerchCache(result);
            items.value = result;
        } catch (error) {
            items.value = bakedMerch.items;
        } finally {
            status.value = 'ready';
            controller.abort();
        }
    });

    onBeforeUnmount(() => {
        disposed = true;
        controller.abort();
    });
</script>
